import { Cascader } from "antd";
import Icon from "@ant-design/icons";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { findLayersOrLayerAssets, type Animation } from "@blings/blings-player";
import type { Precomposition } from "@blings/blings-player/lib/src/libs/l/types/assets";
import { useCallback, useEffect, useState } from "react";
import { HoverTooltip } from "../../BaseComps/InfoTooltip";
import { ToggleButton } from "../../BaseComps/ToggleButton";
import { ReactComponent as layerSelectIcon } from "../../icons-and-animations/selectarrow.svg";
import { sharedSelectAllowClear } from "../antd-extensions/selectClearIcon";
import { mainAssetKey } from "../../stores/PlayerStore";
import {
  getAssetDisplayNameOccurrences,
  getAssetLayerPairKey,
} from "../../stores/getAssetLayerPairs";
import { useLayerInspectorSelection } from "./useLayerInspectorSelection";
import "./LayerSelector.scss";
import type { LayerSelectorOption } from "../../helpers/layerSelectorFilters";

type AssetLookup = {
  byId: Map<string, Precomposition[]>;
  byName: Map<string, Precomposition[]>;
  nameOccurrences: Record<string, number>;
};

interface CascadeOption {
  value: string;
  label?: React.ReactNode;
  label_without_number_selected?: React.ReactNode;
  label_to_search?: string;
  label_to_show_on_tag?: React.ReactNode;
  disabled?: boolean;
  children?: CascadeOption[];
  // Determines if this is a leaf node(effective when `loadData` is specified).
  // `false` will force trade TreeNode as a parent node.
  // Show expand icon even if the current node has no children.
  isLeaf?: boolean;
}

type AssetLayerPair = { nm: string; uid?: number };

interface LayerSelectorProps {
  isMulti: boolean;
  subSelector?: boolean;
  jsonVid?: Animation;
  placeholder?: string;
  assetLayerPairs: Iterable<[string, AssetLayerPair[]]>;
  selectedAssetId?: string;
  selectedLayerName: string;
  selectedLayerUid?: number;
  selectedAdditionals?: Array<{
    assetId?: string;
    layerName: string;
    layerUid?: number;
  }>;
  onChange: (
    index: number,
    assetId?: string,
    layerName?: string,
    layerUid?: number,
    additionals?: Array<{ assetId?: string; layerName: string; layerUid?: number }>
  ) => void;
  index: number;
  mapFilterLayerFunc?: (
    p: Array<LayerSelectorOption>,
    layerName: string,
    assetId?: string,
    layerUid?: number,
    additionals?: Array<{
      assetId?: string;
      layerName: string;
      layerUid?: number;
    }>
  ) => Array<LayerSelectorOption>;
}

type ResolveAssetKeyForSelectionArgs = {
  selectedAssetId: string;
  layerName: string;
  layerUid?: number;
  assetLookup: AssetLookup;
  availableAssetKeys: Set<string>;
};

type BuildLayerSelectorSelectionsArgs = Pick<
  LayerSelectorProps,
  | "assetLayerPairs"
  | "selectedAssetId"
  | "selectedLayerName"
  | "selectedLayerUid"
  | "selectedAdditionals"
  | "jsonVid"
  | "mapFilterLayerFunc"
>;

type SelectableLayerSelectionPath = {
  assetId: string;
  layerName: string;
  layerUid?: number;
  path: string[];
};

type LayerRef = {
  assetId?: string;
  layerName?: string;
  layerUid?: number;
};

type DecodedOptionValue = {
  assetId?: string;
  layerName?: string;
  layerUid?: number;
};

type DecodeOptionValueResult =
  | { ok: true; value: DecodedOptionValue }
  | { ok: false; reason: "missing" | "parse_error" };

export const LayerSelector = observer(function LayerSelector({
  isMulti,
  placeholder,
  assetLayerPairs,
  selectedAssetId,
  selectedLayerName,
  selectedLayerUid,
  selectedAdditionals,
  jsonVid,
  onChange,
  index,
  subSelector,
  mapFilterLayerFunc, //= ()=> true
}: LayerSelectorProps) {
  const assetLayerPairSignature =
    getAssetLayerPairSignature(assetLayerPairs);
  // State declarations
  const [selectedLayers, setSelectedLayers] = useState<string[][] | undefined>(
    () =>
      buildLayerSelectorSelections({
        assetLayerPairs,
        selectedAssetId,
        selectedLayerName,
        selectedLayerUid,
        selectedAdditionals,
        jsonVid,
        mapFilterLayerFunc,
      })
  );
  const [allCompositions, setAllCompositions] = useState<any[]>([]);
  const [selectableOptions, setSelectableOptions] = useState<CascadeOption[]>(
    []
  );

  // Function declarations
  const handleChange = useCallback(
    (_value) => {
      // The value is a string array but we cant use as a str[] because of antd internals
      const value = _value as any[];
      const changeInput: {
        assetId?: string;
        layerName?: string;
        layerUid?: number;
        additionals: { assetId?: string; layerName: string; layerUid?: number }[];
      } = { assetId: undefined, layerName: undefined, layerUid: undefined, additionals: [] };
      /**
       * Structure of the value array
       * [
       *    [selectedOptionValue], // When a option is selected in the root
       *    [selectedComp, selectedOptionValue], // When a option is selected as the child of a composition
       *    [selectedComp] // When all options of that composition are selected
       * ]
       */
      if (value.length === 0)
        onChange(index, undefined, undefined, undefined, undefined);
      else {
        const seenAdditionals = new Set<string>();

        const appendAdditional = (layer: LayerRef | undefined) => {
          if (!layer?.layerName) return;
          const key = `${layer.assetId || ""}|${layer.layerName}|${
            typeof layer.layerUid === "number" ? layer.layerUid : ""
          }`;
          if (seenAdditionals.has(key)) return;
          seenAdditionals.add(key);
          changeInput.additionals.push({
            assetId: layer.assetId,
            layerName: layer.layerName,
            layerUid: layer.layerUid,
          });
        };

        const applyOptionSelection = (
          optionValue: string | undefined,
          isPrimarySelection: boolean
        ): boolean => {
          const decodedResult = decodeOptionValue(optionValue);
          if (!decodedResult.ok) {
            if (isPrimarySelection) {
              return false;
            }
            return true;
          }

          const decoded = decodedResult.value;
          if (!decoded.layerName) {
            if (isPrimarySelection) {
              return false;
            }
            return true;
          }
          const primaryRef: LayerRef = {
            assetId:
              decoded.assetId === mainAssetKey ? undefined : decoded.assetId,
            layerName: decoded.layerName,
            layerUid: decoded.layerUid,
          };

          if (isPrimarySelection) {
            changeInput.assetId = primaryRef.assetId;
            changeInput.layerName = primaryRef.layerName;
            changeInput.layerUid = primaryRef.layerUid;
          } else {
            appendAdditional(primaryRef);
          }
          return true;
        };

        const mainOptionValue = getSelectionOptionValue(value[0]);
        const hasValidPrimarySelection = applyOptionSelection(
          mainOptionValue,
          true
        );
        if (!hasValidPrimarySelection) return;

        for (let i = 1; i < value.length; i++) {
          const optionValue = getSelectionOptionValue(value[i]);
          applyOptionSelection(optionValue, false);
        }

        onChange(
          index,
          changeInput.assetId,
          changeInput.layerName,
          changeInput.layerUid,
          changeInput.additionals
        );
      }
      const selectedCompositionCount = getSelectedCompositionCount(value);
      setSelectableOptions((prev) => {
        return prev.map((option) => {
          if (option.value in selectedCompositionCount) {
            return {
              ...option,
              label: (
                <>
                  {option.label_without_number_selected}{" "}
                  <span style={{ color: "rgba(245, 34, 45, 1)" }}>
                    ({selectedCompositionCount[option.value]})
                  </span>
                </>
              ),
            };
          } else {
            return {
              ...option,
              label: option.label_without_number_selected,
            };
          }
        });
      });

      setSelectedLayers(value);
    },
    [index, onChange]
  );

  const {
    canSelectFromPlayer,
    selectionFromPlayerEnabled,
    startSelectionCapture,
    stopSelectionCapture,
  } = useLayerInspectorSelection({
    jsonVid,
    isMulti,
    selectableOptions,
    selectedLayers,
    handleChange,
  });

  // useEffect declarations
  useEffect(() => {
    // Set all compositions
    setAllCompositions(Array.from(toJS<[string, any]>(assetLayerPairs)));
  }, [assetLayerPairs, assetLayerPairSignature]);

  useEffect(() => {
    // Set the relevant compositions
    const obj: { [key: string]: CascadeOption } = {};
    const _selectableOptions: CascadeOption[] = [];
    allCompositions.forEach(([assetId, layers]) => {
      if (mapFilterLayerFunc) {
        const reducedLayers = layers.reduce<LayerSelectorOption[]>(
          (p, c) => mapFilterLayerFunc(p, c.nm, assetId, c?.uid, []),
          []
        );
        // Set the selectable options. This will be used to populate the cascader
        reducedLayers.forEach((layerNameOrTextRender) => {
          const data = layerNameOrTextRender as LayerSelectorOption;
          const parent = data.parent as string;
          const parentLabel = data.parentLabel || parent;
          const parentKey = parent;
          const child = data.child;
          const optionValue = createLayerOptionValue({
            assetId,
            layerName: data.layerName,
            layerUid: data.layerUid,
            renderText: data.renderText,
          });

          if (child) {
            if (!Object.keys(obj).includes(parentKey)) {
              obj[parentKey] = {
                value: parentKey,
                children: [],
                label: parentLabel,
                label_to_search: parentLabel,
                label_without_number_selected: parentLabel,
              };
            }
            const existingChildIndex =
              obj[parentKey].children?.findIndex(
                (existingChild) => existingChild.value === optionValue
              ) ?? -1;

            if (existingChildIndex < 0) {
              obj[parentKey].children?.push({
                value: optionValue,
                label_to_search: child,
                label: child,
                label_to_show_on_tag: (
                  <span title={child + " / " + parentLabel}>
                    {child}{" "}
                    <span style={{ color: "rgba(92, 92, 92, 1)" }}>
                      {" "}
                      / {parentLabel}
                    </span>
                  </span>
                ),
                label_without_number_selected: child,
                children: [],
              });
            }
          } else {
            const leafKey = `\x00leaf\x00${optionValue}`;
            if (!Object.keys(obj).includes(leafKey)) {
              obj[leafKey] = {
                value: optionValue,
                children: [],
                label: parentLabel,
                label_to_search: parentLabel,
                label_without_number_selected: parentLabel,
              };
            }
          }
        });
      } else {
        layers.forEach((layerNameOrTextRender, layerIndex) => {
          const layerName = layerNameOrTextRender.nm;
          const layerUid = layerNameOrTextRender?.uid;
          const text = `${layerName}`;

          const optionValue = createLayerOptionValue({
            assetId,
            layerName,
            layerUid,
            renderText: `${text} - ${assetId}`,
          });

          if (assetId !== mainAssetKey) {
            if (!Object.keys(obj).includes(assetId)) {
              obj[assetId] = {
                value: optionValue,
                children: [],
                label: assetId,
                label_to_search: assetId,
                label_without_number_selected: assetId,
              };
            }
            if (
              obj[assetId].children?.findIndex(
                (child) => child.value === optionValue
              ) === -1
            ) {
              obj[assetId].children?.push({
                value: optionValue,
                label_to_search: text,
                label: text,
                label_to_show_on_tag: (
                  <span title={text + " / " + assetId}>
                    {text}{" "}
                    <span style={{ color: "rgba(92, 92, 92, 1)" }}>
                      {" "}
                      / {assetId}
                    </span>
                  </span>
                ),
                label_without_number_selected: text,
                children: [],
              });
              obj[assetId].value = assetId;
            }
          } else {
            const key = getLeafOptionKey(optionValue);
            if (
              !Object.values(obj).some((item) => item.value === optionValue)
            ) {
              obj[key] = {
                value: optionValue,
                label: text,
                label_to_search: text,
                label_without_number_selected: text,
                children: [],
              };
            }
          }
        });
      }
    });
    const selectedCompositionCount = getSelectedCompositionCount(
      selectedLayers
    );

    for (const key in obj) {
      if (Object.keys(selectedCompositionCount).includes(key)) {
        obj[key].label = (
          <>
            {obj[key].label_without_number_selected}{" "}
            <span style={{ color: "rgba(245, 34, 45, 1)" }}>
              ({selectedCompositionCount[obj[key].value]})
            </span>
          </>
        );
      }
      _selectableOptions.push(obj[key]);
    }
    setSelectableOptions(_selectableOptions);
  }, [allCompositions, mapFilterLayerFunc, selectedLayers]);

  // Extra logic
  function displayRender(label, selectedOptions) {
    if (!selectedOptions[selectedOptions.length - 1]) {
      return <span title={label}>{label}</span>;
    }
    return (
      <span>
        {selectedOptions[selectedOptions.length - 1].label_to_show_on_tag
          ? selectedOptions[selectedOptions.length - 1].label_to_show_on_tag
          : selectedOptions[selectedOptions.length - 1]
              .label_without_number_selected}
      </span>
    );
  }

  function customSearch(
    inputValue: string,
    options: CascadeOption[],
    fieldNames
  ) {
    for (let i = 0; i < options.length; i++) {
      if (
        options[i].label_to_search
          ?.toLowerCase()
          .includes(inputValue.toLowerCase())
      ) {
        return true;
      }
    }
    return false;
  }

  function renderSearch(inputValue, path, prefixCls, fieldNames) {
    const jsxToRender: JSX.Element[] = [];

    let renderText = "";
    for (let i = 0; i < path.length; i++) {
      renderText += path[i].label_to_search + " / ";
    }
    renderText = renderText.slice(0, renderText.length - 3);

    // for (let i = 0; i < path.length; i++) {
    const matchingText = inputValue;
    const matchingTextStartIndex = renderText
      .toLowerCase()
      .indexOf(inputValue.toLowerCase());
    const matchingTextEndIndex = matchingTextStartIndex + inputValue.length;
    const textBeforeMatchingText = renderText.slice(0, matchingTextStartIndex);
    const textAfterMatchingText = renderText.slice(matchingTextEndIndex);
    jsxToRender.push(
      <>
        {textBeforeMatchingText}
        <span style={{ color: "rgba(255, 92, 92, 1)" }}>{matchingText}</span>
        {textAfterMatchingText}
      </>
    );
    // }
    return jsxToRender;
  }

  return (
    <div className="LayerSelector" style={{ width: "100%" }}>
      <Cascader
        className="LayerSelector-cascader blings-select-controls"
        options={selectableOptions}
        onChange={(a) => {
          !isMulti ? handleChange([a]) : handleChange(a);
        }}
        dropdownClassName={subSelector ? "subSelector" : ""}
        expandTrigger="hover"
        // maxTagCount="responsive"
        multiple={isMulti}
        // dropdownMatchSelectWidth={true}
        showCheckedStrategy="SHOW_CHILD"
        placeholder={placeholder || isMulti ? "Choose layers" : "Choose layer"}
        showSearch={{
          filter: customSearch,
          render: renderSearch,
        }}
        allowClear={sharedSelectAllowClear}
        displayRender={displayRender}
        value={
          selectedLayers && selectedLayers.length > 0
            ? selectedLayers
            : undefined
        }
      />
      <HoverTooltip
        title="Select From Player"
        description="Turn this on, then click layers in the player canvas to use them in this connector."
      >
        <span className="LayerSelector-select-toggle-wrapper">
          <ToggleButton
            size="small"
            className="LayerSelector-select-toggle"
            isOn={selectionFromPlayerEnabled}
            disabled={!canSelectFromPlayer}
            onToggle={(nextState) => {
              if (nextState) startSelectionCapture();
              else stopSelectionCapture();
            }}
            icon={
              <Icon
                component={layerSelectIcon}
                style={{ fontSize: 13, display: "block" }}
              />
            }
            aria-label="Select layer from player"
          />
        </span>
      </HoverTooltip>
    </div>
  );
});

interface BuildInitialSelectionArgs {
  assetId: string;
  layerName: string;
  layerUid?: number;
  jsonVid?: Animation;
  assetLayerPairs: LayerSelectorProps["assetLayerPairs"];
  mapFilterLayerFunc?: LayerSelectorProps["mapFilterLayerFunc"];
  lookupAssetId?: string;
}

function getAssetLayerPairEntries(
  assetLayerPairs: LayerSelectorProps["assetLayerPairs"]
): Array<[string, AssetLayerPair[]]> {
  return Array.from(toJS<[string, AssetLayerPair[]]>(assetLayerPairs));
}

function getAssetLayerPairSignature(
  assetLayerPairs: LayerSelectorProps["assetLayerPairs"]
): string {
  return JSON.stringify(getAssetLayerPairEntries(assetLayerPairs));
}

export function buildLayerSelectorSelections({
  assetLayerPairs,
  selectedAssetId,
  selectedLayerName,
  selectedLayerUid,
  selectedAdditionals,
  jsonVid,
  mapFilterLayerFunc,
}: BuildLayerSelectorSelectionsArgs): string[][] {
  const availableAssetKeys = new Set(
    getAssetLayerPairEntries(assetLayerPairs).map(([assetKey]) => {
      return assetKey;
    })
  );
  const assetLookup = buildAssetLookup(jsonVid);
  const selectableLayerSelectionPaths = buildSelectableLayerSelectionPaths({
    assetLayerPairs,
    mapFilterLayerFunc,
  });
  const rawAssetId = selectedAssetId ? selectedAssetId : mainAssetKey;
  const assetId = resolveAssetKeyForSelection({
    selectedAssetId: rawAssetId,
    layerName: selectedLayerName,
    layerUid: selectedLayerUid,
    assetLookup,
    availableAssetKeys,
  });
  const initialSelections: string[][] = [];

  if (selectedLayerName) {
    const value = buildInitialLayerSelectionPath({
      assetId,
      layerName: selectedLayerName,
      layerUid: selectedLayerUid,
      jsonVid,
      assetLayerPairs,
      mapFilterLayerFunc,
      lookupAssetId: assetId,
      selectableLayerSelectionPaths,
    });
    initialSelections.push(value);
  }

  selectedAdditionals?.forEach((additional) => {
    const rawAdditionalAssetId = additional.assetId
      ? additional.assetId
      : mainAssetKey;
    const additionalAssetId = resolveAssetKeyForSelection({
      selectedAssetId: rawAdditionalAssetId,
      layerName: additional.layerName,
      layerUid: additional.layerUid,
      assetLookup,
      availableAssetKeys,
    });
    const value = buildInitialLayerSelectionPath({
      assetId: additionalAssetId,
      layerName: additional.layerName,
      layerUid: additional.layerUid,
      jsonVid,
      assetLayerPairs,
      mapFilterLayerFunc,
      lookupAssetId: additionalAssetId,
      selectableLayerSelectionPaths,
    });
    initialSelections.push(value);
  });

  return initialSelections;
}

function buildSelectableLayerSelectionPaths({
  assetLayerPairs,
  mapFilterLayerFunc,
}: Pick<
  BuildLayerSelectorSelectionsArgs,
  "assetLayerPairs" | "mapFilterLayerFunc"
>): SelectableLayerSelectionPath[] {
  const paths: SelectableLayerSelectionPath[] = [];

  getAssetLayerPairEntries(assetLayerPairs).forEach(([assetId, layers]) => {
    if (mapFilterLayerFunc) {
      layers
        .reduce<LayerSelectorOption[]>(
          (p, c) => mapFilterLayerFunc(p, c.nm, assetId, c?.uid, []),
          []
        )
        .forEach((layerOption) => {
          const optionValue = createLayerOptionValue({
            assetId,
            layerName: layerOption.layerName,
            layerUid: layerOption.layerUid,
            renderText: layerOption.renderText,
          });
          paths.push({
            assetId,
            layerName: layerOption.layerName,
            layerUid: layerOption.layerUid,
            path: layerOption.child
              ? [layerOption.parent as string, optionValue]
              : [optionValue],
          });
        });
      return;
    }

    layers.forEach((layer) => {
      const layerName = layer.nm;
      const layerUid = layer.uid;
      const optionValue = createLayerOptionValue({
        assetId,
        layerName,
        layerUid,
        renderText: `${layerName} - ${assetId}`,
      });
      paths.push({
        assetId,
        layerName,
        layerUid,
        path: assetId !== mainAssetKey ? [assetId, optionValue] : [optionValue],
      });
    });
  });

  return paths;
}

function buildAssetLookup(jsonVid?: Animation): AssetLookup {
  const assets = (jsonVid?.assets ?? []).filter(
    (a): a is Precomposition => Array.isArray((a as Precomposition).layers)
  );
  const byId = new Map<string, Precomposition[]>();
  const byName = new Map<string, Precomposition[]>();

  for (const asset of assets) {
    if (asset.id) {
      let list = byId.get(asset.id);
      if (!list) {
        list = [];
        byId.set(asset.id, list);
      }
      list.push(asset);
    }
    if (asset.nm) {
      let list = byName.get(asset.nm);
      if (!list) {
        list = [];
        byName.set(asset.nm, list);
      }
      list.push(asset);
    }
  }

  return {
    byId,
    byName,
    nameOccurrences: getAssetDisplayNameOccurrences(assets),
  };
}

function resolveAssetKeyForSelection({
  selectedAssetId,
  layerName,
  layerUid,
  assetLookup,
  availableAssetKeys,
}: ResolveAssetKeyForSelectionArgs): string {
  if (!selectedAssetId || selectedAssetId === mainAssetKey) {
    return mainAssetKey;
  }

  if (availableAssetKeys.has(selectedAssetId)) {
    return selectedAssetId;
  }

  const candidateSet = new Set<Precomposition>();
  for (const a of assetLookup.byId.get(selectedAssetId) ?? []) candidateSet.add(a);
  for (const a of assetLookup.byName.get(selectedAssetId) ?? []) candidateSet.add(a);
  const candidateAssets = Array.from(candidateSet);

  if (!candidateAssets.length) {
    return selectedAssetId;
  }

  const matchedByUid =
    typeof layerUid === "number"
      ? candidateAssets.find((asset) =>
          asset.layers?.some((layer) => layer?.uid === layerUid)
        )
      : undefined;
  const matchedByName =
    !matchedByUid && layerName
      ? candidateAssets.find((asset) =>
          asset.layers?.some((layer) => layer?.nm === layerName)
        )
      : undefined;
  const resolvedAsset = matchedByUid || matchedByName || candidateAssets[0];
  const resolvedKey = getAssetLayerPairKey(resolvedAsset, assetLookup.nameOccurrences);

  if (availableAssetKeys.has(resolvedKey)) {
    return resolvedKey;
  }

  return resolvedAsset.id || selectedAssetId;
}

function resolveCurrentLayerNameForSelection({
  assetLayerPairs,
  assetId,
  layerName,
  layerUid,
}: {
  assetLayerPairs: LayerSelectorProps["assetLayerPairs"];
  assetId: string;
  layerName: string;
  layerUid?: number;
}) {
  if (typeof layerUid !== "number") {
    return layerName;
  }

  const layers = getAssetLayerPairEntries(assetLayerPairs).find(
    ([currentAssetId]) => currentAssetId === assetId
  )?.[1];
  const currentLayer = layers?.find((layer) => layer.uid === layerUid);
  return currentLayer?.nm || layerName;
}

function buildInitialLayerSelectionPath({
  assetId,
  layerName,
  layerUid,
  jsonVid,
  assetLayerPairs,
  mapFilterLayerFunc,
  lookupAssetId,
  selectableLayerSelectionPaths,
}: BuildInitialSelectionArgs & {
  selectableLayerSelectionPaths: SelectableLayerSelectionPath[];
}): string[] {
  let resolvedLayerUid = layerUid;
  if (resolvedLayerUid === undefined && jsonVid) {
    const layerData = findLayersOrLayerAssets({
      jsonVid,
      layerName,
      assetId: lookupAssetId ?? assetId,
    });
    resolvedLayerUid = layerData ? layerData[0]?.uid : undefined;
  }
  const currentLayerName = resolveCurrentLayerNameForSelection({
    assetLayerPairs,
    assetId,
    layerName,
    layerUid: resolvedLayerUid,
  });
  const selectablePath = findSelectableLayerSelectionPath({
    selectableLayerSelectionPaths,
    assetId,
    layerName: currentLayerName,
    layerUid: resolvedLayerUid,
  });
  if (selectablePath) return selectablePath;

  const hashValue = hashFromAssetIDAndLayerName(
    assetId,
    currentLayerName,
    resolvedLayerUid
  );
  const renderText = GetLayerRenderText(
    hashValue,
    assetLayerPairs,
    mapFilterLayerFunc
  );
  const optionValue = createLayerOptionValue({
    assetId,
    layerName: currentLayerName,
    layerUid: resolvedLayerUid,
    renderText,
    hashValue,
  });
  return buildSelectionPath(assetId, optionValue);
}

function findSelectableLayerSelectionPath({
  selectableLayerSelectionPaths,
  assetId,
  layerName,
  layerUid,
}: {
  selectableLayerSelectionPaths: SelectableLayerSelectionPath[];
  assetId: string;
  layerName: string;
  layerUid?: number;
}): string[] | undefined {
  const exactLayerUidMatch =
    typeof layerUid === "number"
      ? selectableLayerSelectionPaths.find(
          (entry) => entry.assetId === assetId && entry.layerUid === layerUid
        )
      : undefined;
  const layerNameMatch =
    exactLayerUidMatch ??
    selectableLayerSelectionPaths.find(
      (entry) => entry.assetId === assetId && entry.layerName === layerName
    );

  return layerNameMatch?.path.slice();
}

function buildSelectionPath(assetId: string, selectionValue: string): string[] {
  return assetId !== mainAssetKey ? [assetId, selectionValue] : [selectionValue];
}

interface CreateLayerOptionValueArgs {
  assetId: string;
  layerName: string;
  layerUid?: number;
  renderText: string;
  hashValue?: number;
}

function createLayerOptionValue({
  assetId,
  layerName,
  layerUid,
  renderText,
  hashValue,
}: CreateLayerOptionValueArgs) {
  const serializedHash =
    (hashValue ?? hashFromAssetIDAndLayerName(assetId, layerName, layerUid)) +
    "_" +
    renderText;
  const optionValue: DecodedOptionValue & { hash: { value: string } } = {
    assetId,
    layerName,
    layerUid,
    hash: {
      value: serializedHash,
    },
  };
  return JSON.stringify(optionValue);
}

function getSelectionOptionValue(selection: any): string | undefined {
  if (Array.isArray(selection)) {
    if (!selection.length) return undefined;
    return selection[selection.length - 1];
  }
  return typeof selection === "string" ? selection : undefined;
}

function decodeOptionValue(
  optionValue: string | undefined
): DecodeOptionValueResult {
  if (!optionValue) return { ok: false, reason: "missing" };
  try {
    return {
      ok: true,
      value: JSON.parse(optionValue) as DecodedOptionValue,
    };
  } catch {
    return { ok: false, reason: "parse_error" };
  }
}

function getSelectedCompositionCount(values?: any[]) {
  if (!values || values.length === 0) return {};
  return values.reduce<Record<string, number>>((acc, selection) => {
    if (Array.isArray(selection) && selection.length === 2) {
      const composition = selection[0];
      acc[composition] = (acc[composition] || 0) + 1;
    }
    return acc;
  }, {});
}

function getLeafOptionKey(optionValue: string): string {
  return `\x00leaf\x00${optionValue}`;
}

/**
 * Get the render text for a specific hash value
 * @param hashValue Receives the hash value of the layer
 * @returns Returns the render text of the layer
 */
function GetLayerRenderText(
  hashValue: number,
  assetLayerPairs: LayerSelectorProps["assetLayerPairs"],
  mapFilterLayerFunc: LayerSelectorProps["mapFilterLayerFunc"]
): string {
  let result = "";
  getAssetLayerPairEntries(assetLayerPairs).flatMap(
    (
      [assetId, layers]: [
        string,
        AssetLayerPair[]
      ],
      assetIndex
    ) =>
      mapFilterLayerFunc
        ? layers
            .reduce<LayerSelectorOption[]>(
              (p, c) => mapFilterLayerFunc(p, c.nm, assetId, c?.uid, []),
              []
            )
            .map((layerNameOrTextRender, layerIndex) => {
              const layerName = (layerNameOrTextRender as LayerSelectorOption)
                .layerName;

              const text = (layerNameOrTextRender as LayerSelectorOption)
                .renderText;
              const layerUid = (layerNameOrTextRender as LayerSelectorOption)
                .layerUid;
              if (
                hashValue ===
                hashFromAssetIDAndLayerName(assetId, layerName, layerUid)
              ) {
                result = text;
                return text;
              }
              return "";
            })
        : layers.map((layerNameOrTextRender, layerIndex) => {
            const layerName = layerNameOrTextRender.nm;
            const layerUid = layerNameOrTextRender.uid;
            const text = `${layerName} - ${assetId}`;
            if (
              hashValue ===
              hashFromAssetIDAndLayerName(assetId, layerName, layerUid)
            ) {
              result = text;
              return text;
            }
            return "";
          })
  );
  return result;
}

/**
 * Create a random hash using the asset id and layer name as the seeds
 * @param assetId The asset id to use in the hash function
 * @param layerName The layer name to use in the hash function
 * @returns The hash of the asset id and layer name
 */
export function hashFromAssetIDAndLayerName(
  assetId,
  layerName,
  layerUid?: number
) {
  let hash = 0;
  if (assetId.length === 0) return hash;
  for (let i = 0, j = 0; j < assetId.length; i++, j++) {
    let char = assetId.charCodeAt(i);

    // If the char at the i index does not exist, then we have reached the end of the string and should start over
    if (char === undefined) {
      i = 0;
      char = assetId.charCodeAt(i);
    }
    // Create the hash
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  if (layerName.length === 0) return hash;
  for (let i = 0, j = 0; j < layerName.length; i++, j++) {
    let char = layerName.charCodeAt(i);

    // If the char at the i index does not exist, then we have reached the end of the string and should start over
    if (char === undefined) {
      i = 0;
      char = layerName.charCodeAt(i);
    }

    // Create the hash
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  let layerUidString = layerUid?.toLocaleString() || "";
  if (layerUidString?.length === 0) return hash;

  for (let i = 0, j = 0; j < layerUidString.length; i++, j++) {
    let char = layerUidString.charCodeAt(i);

    if (char === undefined) {
      i = 0;
      char = layerUidString.charCodeAt(i);
    }

    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return hash;
}
